import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelectContext } from "./Select";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  content: {
    position: "absolute",
    border: "1px solid #DCDCDC",
    backgroundColor: "#fff",
    zIndex: 1300, // on top of Dialog
    maxHeight: "200px",
    overflowY: "auto",
    marginTop: "4px",
    borderRadius: "4px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "4px",
    minWidth: "250px", // Ensure minimum width for readability
    whiteSpace: "nowrap", // Prevent text wrapping to maintain proper width
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },
}));

const SelectContent = ({ children }) => {
  const { open, close, triggerRef, minWidth } = useSelectContext();
  const classes = useStyles();
  const [position, setPosition] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    
    // Set initial width to at least trigger width, but allow content to expand
    const calculatedWidth = Math.max(rect.width, minWidth || 0, 250); // Increased minimum to accommodate "Bulk Update Price Bundles"
    
    // Check if dropdown would extend beyond viewport
    const viewportWidth = window.innerWidth;
    const wouldOverflow = rect.left + calculatedWidth > viewportWidth;
    
    // Position dropdown to the left if it would overflow, otherwise to the right
    const left = wouldOverflow 
      ? rect.right - calculatedWidth + window.scrollX  // Align right edge of dropdown with right edge of trigger
      : rect.left + window.scrollX;                    // Normal left alignment
    
    setPosition({
      top: rect.bottom + window.scrollY,
      left: left,
      width: calculatedWidth,
    });

    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target) && !triggerRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    function updatePosition() {
      const rect = triggerRef.current.getBoundingClientRect();
      const calculatedWidth = Math.max(rect.width, minWidth || 0, 250);
      
      // Check viewport overflow on position update as well
      const viewportWidth = window.innerWidth;
      const wouldOverflow = rect.left + calculatedWidth > viewportWidth;
      
      const left = wouldOverflow 
        ? rect.right - calculatedWidth + window.scrollX
        : rect.left + window.scrollX;
      
      setPosition({
        top: rect.bottom + window.scrollY,
        left: left,
        width: calculatedWidth,
      });
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, close, triggerRef, minWidth]);

  if (!open || !position) return null;

  return ReactDOM.createPortal(
    <div
      ref={contentRef}
      className={classes.content}
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default SelectContent;
